import { useState, type CSSProperties, type JSX, type KeyboardEvent } from 'react'
import { Dialog, DialogClose, DialogContent } from '@ttab/elephant-ui'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LoaderIcon,
  XIcon
} from '@ttab/elephant-ui/icons'
import { useTranslation } from 'react-i18next'
import { Preview } from '../Preview'
import type { PreviewNavigation } from '../hooks/usePreviewNavigation'
import type { ImageSearchHit } from '../lib/types'
import type { PreviewPlacement } from '../lib/previewPlacement'

const ROUND_BUTTON = 'rounded-full bg-background/80 backdrop-blur-sm p-1.5 shadow-sm '
  + 'hover:bg-background transition-colors'

export const PreviewDialog = ({
  hit,
  hasPrev,
  hasNext,
  isLoadingNext,
  close,
  prev,
  next,
  up,
  down,
  placement
}: PreviewNavigation & { placement: PreviewPlacement | undefined }): JSX.Element => {
  const { t } = useTranslation('views')

  // Centred in the free space beside the view, with the preview's width capped to fit there
  const style: (CSSProperties & Record<'--preview-space', string>) | undefined = placement && {
    left: placement.center,
    '--preview-space': `${placement.space}px`
  }

  // Keep the last hit rendered while the dialog animates out
  const [shownHit, setShownHit] = useState<ImageSearchHit | undefined>(hit)

  if (hit && hit !== shownHit) {
    setShownHit(hit)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      prev()
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      next()
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      up()
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      down()
    }
  }

  return (
    <Dialog
      open={!!hit}
      onOpenChange={(open) => {
        if (!open) {
          close()
        }
      }}
    >
      <DialogContent
        className='z-50 gap-0 p-0 overflow-hidden w-fit md:w-fit max-w-none'
        style={style}
        onKeyDown={onKeyDown}
      >
        <DialogClose className={`absolute right-3 top-3 z-10 ${ROUND_BUTTON}`}>
          <XIcon size={16} strokeWidth={1.75} />
          <span className='sr-only'>{t('imageSearch.labels.close')}</span>
        </DialogClose>
        {shownHit && (
          <Preview
            hit={shownHit}
            overlay={(
              <>
                {hasPrev && (
                  <button
                    type='button'
                    onClick={prev}
                    className={`absolute left-2 top-1/2 -translate-y-1/2 ${ROUND_BUTTON}`}
                  >
                    <ChevronLeftIcon size={20} strokeWidth={1.75} />
                    <span className='sr-only'>{t('imageSearch.labels.previous')}</span>
                  </button>
                )}
                {hasNext && (
                  <button
                    type='button'
                    onClick={next}
                    aria-disabled={isLoadingNext}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 ${ROUND_BUTTON}`}
                  >
                    {isLoadingNext
                      ? <LoaderIcon size={20} strokeWidth={1.75} className='animate-spin' />
                      : <ChevronRightIcon size={20} strokeWidth={1.75} />}
                    <span className='sr-only'>{t('imageSearch.labels.next')}</span>
                  </button>
                )}
              </>
            )}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
